import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import styles from '../SettingsMenu.css';
import { TextInput } from 'components/common/input';
import { getLocalUsername, getLocalColor, getLocalAvatar } from 'reducers/settings';
import { USERNAME_MAX_LEN } from 'constants/settings';
import { setUsername, setColor } from 'actions/settings';
import { t } from 'locale';
import { UserAvatar, AvatarBadge } from '../../lobby/UserAvatar';
import { ExternalLink } from 'components/common/link';
import { Trans } from 'react-i18next';
import { server_updateUser } from '../../../lobby/actions/users';
import Tooltip from '@material-ui/core/Tooltip';
import { AccountService } from 'account/account';
import { usePatronTier } from 'account/hooks';
import { LogoutButton } from 'components/account/buttons';
import { useAppContext } from 'appContext';
import { CreateAvatar } from 'components/lobby/CreateAvatar';
import { createAvatar, selectAvatar, removeAvatar } from 'lobby/actions/user-avatar';
const ProfileSettings = props => {
    const { avatarRegistry } = useAppContext();
    const propsRef = useRef(props);
    const dirtyRef = useRef(false);
    const usernameInputRef = useRef(null);
    const [avatars, setAvatars] = useState(avatarRegistry.getAll());
    const tier = usePatronTier();
    const tierRef = useRef(tier);
    useEffect(function onTierChange() {
        if (tier !== tierRef.current) {
            // update color on exit
            dirtyRef.current = true;
            tierRef.current = tier;
        }
    }, [tier]);
    useEffect(() => {
        const updateAvatars = () => setAvatars(avatarRegistry.getAll());
        avatarRegistry.on('change', updateAvatars);
        if (avatars !== avatarRegistry.getAll()) {
            // update happened after initial render and before effect callback
            updateAvatars();
        }
        return () => {
            avatarRegistry.off('change', updateAvatars);
        };
    }, [avatars, setAvatars]);
    useEffect(function saveProps() {
        propsRef.current = props;
    }, [props]);
    useEffect(function componentDidMount() {
        return function componentWillUnmount() {
            const { current: props } = propsRef;
            if (dirtyRef.current) {
                props.dispatch(server_updateUser({
                    name: props.username,
                    color: props.color,
                    avatar: props.avatar
                }));
            }
        };
    }, []);
    // TODO: debounce
    const onChangeUsername = () => {
        const username = usernameInputRef.current && usernameInputRef.current.value;
        if (!username)
            return;
        if (username !== props.username) {
            props.dispatch(setUsername(username));
            dirtyRef.current = true;
        }
    };
    const selectedAvatar = props.avatar ? avatarRegistry.getByURI(props.avatar) : null;
    const hasArtist = selectedAvatar ? !!selectedAvatar.artist : false;
    const hasPledged = tier > 0 /* None */;
    const hasCustomAvatar = avatars.some(avatar => avatar.type === "file" /* File */);
    return (React.createElement("section", { className: styles.section },
        React.createElement("label", null, t('avatar')),
        React.createElement("div", { className: styles.avatarContainer },
            React.createElement("div", { className: styles.avatarList },
                avatars.map((avatar, idx) => (React.createElement(UserAvatar, { key: idx, avatar: avatar.uri, disabled: avatar.type === "file" /* File */ ? tier < 2 /* Supporter */ : false, selected: avatar.uri === props.avatar, badge: avatar.type === "file" /* File */ ? (React.createElement(AvatarBadge, { className: styles.removeAvatar, onClick: () => {
                            props.dispatch(removeAvatar(avatar));
                            dirtyRef.current = true;
                        } },
                        React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                            React.createElement("line", { x1: "18", y1: "6", x2: "6", y2: "18" }),
                            React.createElement("line", { x1: "6", y1: "6", x2: "18", y2: "18" })))) : (undefined), onClick: () => {
                        props.dispatch(selectAvatar(avatar));
                        dirtyRef.current = true;
                    } }))),
                FEATURE_CUSTOM_AVATAR && !hasCustomAvatar && (React.createElement(Tooltip, { title: t('patreonPledgeTierRequired', { tier: 'Supporter' }), placement: "right", disableHoverListener: hasPledged, disableFocusListener: hasPledged, disableTouchListener: hasPledged },
                    React.createElement("div", null,
                        React.createElement(CreateAvatar, { disabled: tier < 2 /* Supporter */, onSelectFile: file => {
                                props.dispatch(createAvatar(file));
                                dirtyRef.current = true;
                            } }))))),
            hasArtist && (React.createElement("div", { className: styles.small },
                React.createElement(Trans, { i18nKey: "avatarCredit", values: { artist: selectedAvatar.artist } },
                    React.createElement("span", { className: styles.blend }, "Selected avatar art by"),
                    selectedAvatar.href ? (React.createElement(ExternalLink, { href: selectedAvatar.href, className: "link-alt" }, "Unknown")) : (React.createElement("span", { className: styles.blend }, "Unknown")))))),
        React.createElement("label", { htmlFor: "profile_username" }, t('displayName')),
        React.createElement(TextInput, { id: "profile_username", theRef: e => (usernameInputRef.current = e), defaultValue: props.username, maxLength: USERNAME_MAX_LEN, onChange: onChangeUsername, onBlur: e => {
                if (usernameInputRef.current) {
                    usernameInputRef.current.value = props.username;
                }
            } }),
        React.createElement("label", { htmlFor: "profile_color" }, t('chatColor')),
        React.createElement(Tooltip, { title: t('patreonPledgeRequired'), placement: "right", disableHoverListener: hasPledged, disableFocusListener: hasPledged, disableTouchListener: hasPledged },
            React.createElement("input", { disabled: !hasPledged, id: "profile_color", type: "color", className: styles.colorSwatch, defaultValue: props.color, onChange: e => {
                    props.dispatch(setColor(e.target.value));
                    dirtyRef.current = true;
                } })),
        hasPledged && (React.createElement("div", null,
            React.createElement(LogoutButton, { onClick: () => AccountService.get().logout() })))));
};
export default connect((state) => {
    return {
        avatar: getLocalAvatar(state),
        username: getLocalUsername(state),
        color: getLocalColor(state)
    };
})(ProfileSettings);
